/**
 * PWA Cache Cleanup Utility
 *
 * Manages cache version control and cleanup for Progressive Web App:
 * - Version tracking in localStorage
 * - Detection of incompatible cache versions
 * - Selective and complete cache clearing
 * - Automatic cleanup on version mismatch
 */

// Constants
export const CACHE_VERSION_KEY = 'pwa-cache-version'

// Type definitions
export interface CacheClearResult {
  success: boolean
  deleted: boolean
  cacheName?: string
  error?: string
}

export interface BulkCacheClearResult {
  cleared: boolean
  cachesCleared: string[]
  errors: Array<{ cacheName: string; error: string }>
  currentVersion: string | null
}

export interface CacheInfo {
  totalCaches: number
  cacheNames: string[]
  currentVersion: string | null
  error?: string
}

export interface DetectAndCleanResult {
  detected: boolean
  cleaned: boolean
  previousVersion: string | null
  newVersion: string
  cachesCleared: string[]
  error?: string
}

/**
 * Get current cache version from localStorage
 * @returns Current version string or null if not set
 */
export function getCurrentCacheVersion(): string | null {
  try {
    return localStorage.getItem(CACHE_VERSION_KEY)
  } catch (error) {
    console.error('Failed to get cache version from localStorage:', error)
    return null
  }
}

/**
 * Set current cache version in localStorage
 * @param version - Version string to store (null to remove)
 */
export function setCurrentCacheVersion(version: string | null): void {
  try {
    if (version === null) {
      localStorage.removeItem(CACHE_VERSION_KEY)
    } else {
      localStorage.setItem(CACHE_VERSION_KEY, version)
    }
  } catch (error) {
    console.error('Failed to set cache version in localStorage:', error)
  }
}

/**
 * Get incompatible cache versions
 * @param currentVersion - The current application version
 * @returns Array of incompatible versions (empty if compatible)
 */
export function getIncompatibleCacheVersions(currentVersion: string): string[] {
  const storedVersion = getCurrentCacheVersion()

  if (!storedVersion) {
    return []
  }

  return storedVersion !== currentVersion ? [storedVersion] : []
}

/**
 * Clear a specific cache by name
 * @param cacheName - Name of the cache to clear
 * @returns Result object indicating success/failure
 */
export async function clearCacheByName(cacheName: string): Promise<CacheClearResult> {
  // Validate input
  if (!cacheName || cacheName.trim() === '') {
    return {
      success: false,
      deleted: false,
      error: 'Cache name cannot be empty'
    }
  }

  try {
    if (!('caches' in window)) {
      return {
        success: false,
        deleted: false,
        error: 'Cache API not supported'
      }
    }

    const cacheExists = await caches.has(cacheName)

    if (!cacheExists) {
      return {
        success: true,
        deleted: false,
        cacheName
      }
    }

    const deleted = await caches.delete(cacheName)

    return {
      success: true,
      deleted,
      cacheName
    }
  } catch (error) {
    return {
      success: false,
      deleted: false,
      cacheName,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Clear all caches when version is incompatible
 * @param newVersion - New cache version to set after clearing
 * @returns Result object with details of cleared caches
 */
export async function clearAllIncompatibleCaches(
  newVersion: string
): Promise<BulkCacheClearResult> {
  const currentVersion = getCurrentCacheVersion()

  // If versions match, no need to clear
  if (currentVersion === newVersion) {
    return {
      cleared: false,
      cachesCleared: [],
      errors: [],
      currentVersion
    }
  }

  try {
    if (!('caches' in window)) {
      return {
        cleared: false,
        cachesCleared: [],
        errors: [{ cacheName: 'all', error: 'Cache API not supported' }],
        currentVersion
      }
    }

    const cacheNames = await caches.keys()
    const clearedCaches: string[] = []
    const errors: Array<{ cacheName: string; error: string }> = []

    // Clear each cache
    for (const cacheName of cacheNames) {
      const result = await clearCacheByName(cacheName)

      if (result.success && result.deleted) {
        clearedCaches.push(cacheName)
      } else if (!result.success) {
        errors.push({
          cacheName,
          error: result.error || 'Unknown error'
        })
      }
    }

    // Update the stored version
    setCurrentCacheVersion(newVersion)

    return {
      cleared: clearedCaches.length > 0,
      cachesCleared: clearedCaches,
      errors,
      currentVersion: newVersion
    }
  } catch (error) {
    return {
      cleared: false,
      cachesCleared: [],
      errors: [{
        cacheName: 'all',
        error: error instanceof Error ? error.message : 'Unknown error'
      }],
      currentVersion
    }
  }
}

/**
 * Clear all caches (including workbox precache)
 * @returns Result object with details of cleared caches
 */
export async function clearAllCaches(): Promise<BulkCacheClearResult> {
  try {
    if (!('caches' in window)) {
      return {
        cleared: false,
        cachesCleared: [],
        errors: [{ cacheName: 'all', error: 'Cache API not supported' }],
        currentVersion: getCurrentCacheVersion()
      }
    }

    const cacheNames = await caches.keys()
    const clearedCaches: string[] = []
    const errors: Array<{ cacheName: string; error: string }> = []

    // Clear each cache
    for (const cacheName of cacheNames) {
      const result = await clearCacheByName(cacheName)

      if (result.success && result.deleted) {
        clearedCaches.push(cacheName)
      } else if (!result.success) {
        errors.push({
          cacheName,
          error: result.error || 'Unknown error'
        })
      }
    }

    return {
      cleared: clearedCaches.length > 0,
      cachesCleared: clearedCaches,
      errors,
      currentVersion: getCurrentCacheVersion()
    }
  } catch (error) {
    return {
      cleared: false,
      cachesCleared: [],
      errors: [{
        cacheName: 'all',
        error: error instanceof Error ? error.message : 'Unknown error'
      }],
      currentVersion: getCurrentCacheVersion()
    }
  }
}

/**
 * Get information about current caches
 * @returns Cache information object
 */
export async function getCacheInfo(): Promise<CacheInfo> {
  try {
    if (!('caches' in window)) {
      return {
        totalCaches: 0,
        cacheNames: [],
        currentVersion: getCurrentCacheVersion(),
        error: 'Cache API not supported'
      }
    }

    const cacheNames = await caches.keys()

    return {
      totalCaches: cacheNames.length,
      cacheNames,
      currentVersion: getCurrentCacheVersion()
    }
  } catch (error) {
    return {
      totalCaches: 0,
      cacheNames: [],
      currentVersion: getCurrentCacheVersion(),
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Detect and clean incompatible caches automatically
 * This should be called on app startup to handle version changes
 * @param currentVersion - The current application cache version
 * @returns Result object with detection and cleanup details
 */
export async function detectAndCleanIncompatibleCaches(
  currentVersion: string
): Promise<DetectAndCleanResult> {
  const previousVersion = getCurrentCacheVersion()

  // First run or version matches - no cleanup needed
  if (!previousVersion || previousVersion === currentVersion) {
    setCurrentCacheVersion(currentVersion)
    return {
      detected: false,
      cleaned: false,
      previousVersion,
      newVersion: currentVersion,
      cachesCleared: []
    }
  }

  // Version mismatch - need to clean
  try {
    const result = await clearAllIncompatibleCaches(currentVersion)

    return {
      detected: true,
      cleaned: result.cleared,
      previousVersion,
      newVersion: currentVersion,
      cachesCleared: result.cachesCleared,
      error: result.errors.length > 0 ? result.errors[0].error : undefined
    }
  } catch (error) {
    return {
      detected: true,
      cleaned: false,
      previousVersion,
      newVersion: currentVersion,
      cachesCleared: [],
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Utility function to check if cache cleanup is needed
 * @param currentVersion - The current application cache version
 * @returns True if cleanup is needed
 */
export function needsCacheCleanup(currentVersion: string): boolean {
  const storedVersion = getCurrentCacheVersion()
  return storedVersion !== null && storedVersion !== currentVersion
}
